import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import {
  firebaseConfigured,
  onAuthChange,
  signOutCurrent,
} from "@/lib/firebase";

type AuthState = {
  user: User | null;
  idToken: string | null;
  ready: boolean;
  // Dev-bypass persona email when no Firebase user is signed in.
  devUser: string | null;
  setDevUser: (email: string | null) => void;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [ready, setReady] = useState(!firebaseConfigured());
  const [devUser, _setDevUser] = useState<string | null>(() =>
    typeof window !== "undefined"
      ? window.localStorage.getItem("cura-studio:dev-user")
      : null,
  );

  // Track previous token state. When the token transitions null → string
  // (Firebase has finished hydrating the persisted session), invalidate every
  // query so screens that fired before the token was available refetch with
  // the proper Authorization header. Fixes the "logged in but no kits" race.
  const prevTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!firebaseConfigured()) {
      setReady(true);
      return;
    }
    return onAuthChange((u, t) => {
      setUser(u);
      setIdToken(t);
      setReady(true);
      if (t && t !== prevTokenRef.current) {
        qc.invalidateQueries();
      }
      prevTokenRef.current = t;
    });
  }, [qc]);

  const setDevUser = (email: string | null) => {
    if (typeof window !== "undefined") {
      if (email) window.localStorage.setItem("cura-studio:dev-user", email);
      else window.localStorage.removeItem("cura-studio:dev-user");
    }
    _setDevUser(email);
  };

  const value: AuthState = {
    user,
    idToken,
    ready,
    devUser,
    setDevUser,
    signOut: async () => {
      await signOutCurrent();
      setUser(null);
      setIdToken(null);
      // Also force any cached queries to re-run with the new (anonymous) state
      qc.invalidateQueries();
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth used outside AuthProvider");
  return v;
}
